import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { ParecerRecursoPage } from './pages/ParecerRecursoPage';
import { DecisaoRecursoPage } from './pages/DecisaoRecursoPage';
import QRCode from 'qrcode';
import {
  getStoredSession,
  logout,
  acceptPolicy,
  changePassword,
  activateFirstAccess,
} from './lib/auth-client';
import { LGP_02_VERSION } from '@aions/core';
import {
  Brand,
  Button,
  ErrorBoundary,
  PolicyAcceptanceModal,
  ChangePasswordForm,
  CHANGE_PASSWORD_MIN_LENGTH,
  FirstAccessForm,
  FIRST_ACCESS_MIN_LENGTH,
  SessionCountdown,
} from '@aions/ui-primitives';
import './styles/global.css';

/**
 * Hash-based routing — leve, sem react-router (paridade web-comite, conserva
 * bundle). Rotas:
 *   #login | #dashboard | #case=:id | #parecer=:id | #decisao=:id
 *
 * Plan 05-03: web-recurso NÃO tem first-access nem triagem (essas rotas vivem
 * no web-comite Phase 2). Apenas as 5 telas listadas acima.
 */
type Route =
  | { kind: 'login' }
  | { kind: 'dashboard' }
  | { kind: 'case'; id: string }
  | { kind: 'parecer'; id: string }
  | { kind: 'decisao'; id: string }
  | { kind: 'change-password' }
  | { kind: 'first-access'; token: string; email: string; otpAuthUri: string | undefined };

function parseHashRoute(): Route | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  if (hash === 'dashboard') return { kind: 'dashboard' };
  if (hash === 'login') return { kind: 'login' };
  if (hash === 'change-password') return { kind: 'change-password' };
  // first-access é PRÉ-AUTH e traz query (#first-access?token=…&email=…&otp=…).
  if (hash.startsWith('first-access')) {
    const qIdx = hash.indexOf('?');
    if (qIdx !== -1) {
      const params = new URLSearchParams(hash.slice(qIdx + 1));
      const token = params.get('token');
      const email = params.get('email');
      const otp = params.get('otp');
      if (token && email) {
        return { kind: 'first-access', token, email, otpAuthUri: otp === null ? undefined : otp };
      }
    }
    return null;
  }

  const eqIdx = hash.indexOf('=');
  if (eqIdx === -1) return null;
  const key = hash.slice(0, eqIdx);
  const id = hash.slice(eqIdx + 1);
  if (!id) return null;
  if (key === 'case') return { kind: 'case', id };
  if (key === 'parecer') return { kind: 'parecer', id };
  if (key === 'decisao') return { kind: 'decisao', id };
  return null;
}

function setHash(route: Route): void {
  switch (route.kind) {
    case 'login':
      window.location.hash = '';
      break;
    case 'dashboard':
      window.location.hash = '#dashboard';
      break;
    case 'case':
      window.location.hash = `#case=${route.id}`;
      break;
    case 'parecer':
      window.location.hash = `#parecer=${route.id}`;
      break;
    case 'decisao':
      window.location.hash = `#decisao=${route.id}`;
      break;
    case 'change-password':
      window.location.hash = '#change-password';
      break;
    case 'first-access':
      // Entrada via link externo — nunca navegamos programaticamente para cá.
      break;
  }
}

function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();

  const [route, setRoute] = useState<Route>(() => {
    const hashRoute = parseHashRoute();
    if (hashRoute) return hashRoute;
    return session ? { kind: 'dashboard' } : { kind: 'login' };
  });

  // Sprint 3.1 — bloqueia tudo até user aceitar LGP-02 (versão atual).
  const needsPolicyAccept =
    session !== null &&
    session.user.policyAccepted === false &&
    route.kind !== 'login' &&
    route.kind !== 'first-access';

  useEffect(() => {
    const onHash = (): void => {
      const hashRoute = parseHashRoute();
      if (hashRoute) setRoute(hashRoute);
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  // Se sessão sumiu (logout), volta para login — exceto no fluxo pré-auth.
  if (!session && route.kind !== 'login' && route.kind !== 'first-access') {
    setRoute({ kind: 'login' });
  }

  const goto = (next: Route): void => {
    setRoute(next);
    setHash(next);
  };

  // first-access: tela pré-auth standalone (sem header/nav), antes do shell normal.
  if (route.kind === 'first-access') {
    return (
      <div className="app">
        <a className="skip-to-main" href="#main-content">
          {t('app.skip_to_main')}
        </a>
        <main id="main-content" className="app-main" tabIndex={-1}>
          <FirstAccessForm
            email={route.email}
            otpAuthUri={route.otpAuthUri}
            minLength={FIRST_ACCESS_MIN_LENGTH}
            makeQrSvg={(uri) => QRCode.toString(uri, { type: 'svg', margin: 1 })}
            labels={{
              title: t('first_access.title'),
              intro: t('first_access.intro'),
              emailPrefix: t('first_access.email_prefix'),
              qrUnavailable: t('first_access.qr_unavailable'),
              qrNotProvided: t('first_access.qr_not_provided'),
              otpCopyLabel: t('first_access.otp_copy_label'),
              passwordLabel: t('first_access.password_label', { min: FIRST_ACCESS_MIN_LENGTH }),
              confirmLabel: t('first_access.confirm_label'),
              totpLabel: t('first_access.totp_label'),
              submit: t('first_access.submit'),
              submitting: t('first_access.submitting'),
              errTooShort: t('first_access.errors.too_short', { min: FIRST_ACCESS_MIN_LENGTH }),
              errMismatch: t('first_access.errors.mismatch'),
              errInvalidTotp: t('first_access.errors.invalid_totp'),
              errTotpIncorrect: t('first_access.errors.totp_incorrect'),
              errAlreadyActivated: t('first_access.errors.already_activated'),
              errNetwork: t('first_access.errors.network'),
              errGeneric: t('first_access.errors.generic'),
            }}
            onSubmit={async (password, totpCode) => {
              const res = await activateFirstAccess(route.token, route.email, password, totpCode);
              if (res.ok) return { ok: true };
              if (res.error === 'invalid_totp') return { ok: false, error: 'invalid_totp' };
              if (res.error === 'already_activated')
                return { ok: false, error: 'already_activated' };
              if (res.error === 'network') return { ok: false, error: 'network' };
              return { ok: false, error: 'other' };
            }}
            onSuccess={() => {
              window.location.hash = '';
              setRoute({ kind: 'login' });
            }}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <a className="skip-to-main" href="#main-content">
        {t('app.skip_to_main')}
      </a>
      <header className="app-header">
        <Brand size="md" tagline={t('app.header_tagline')} />
        {/* Contador de expiração da sessão. Só com sessão viva — nas telas de
            login/primeiro-acesso não há o que contar, e um contador ali
            sugeriria que a sessão já começou. */}
        {session && (
          <SessionCountdown
            token={session.token}
            label={t('app.session_expires_in')}
            expiredLabel={t('app.session_expired')}
          />
        )}
        {session && route.kind !== 'login' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => goto({ kind: 'change-password' })}
            data-testid="nav-change-password"
            style={{ marginLeft: 'auto' }}
          >
            {t('app.change_password')}
          </Button>
        )}
      </header>

      <main id="main-content" className="app-main" tabIndex={-1}>
        <ErrorBoundary
          title={t('error_boundary.title')}
          message={t('error_boundary.message')}
          reloadLabel={t('error_boundary.reload')}
        >
          {route.kind === 'login' && <LoginPage onSuccess={() => goto({ kind: 'dashboard' })} />}
          {route.kind === 'dashboard' && (
            <DashboardPage
              onSelectCase={(id) => goto({ kind: 'case', id })}
              onLogout={() => goto({ kind: 'login' })}
            />
          )}
          {route.kind === 'case' && (
            <CaseDetailPage
              caseId={route.id}
              onBack={() => goto({ kind: 'dashboard' })}
              onParecer={(id) => goto({ kind: 'parecer', id })}
              onDecisao={(id) => goto({ kind: 'decisao', id })}
            />
          )}
          {route.kind === 'parecer' && (
            <ParecerRecursoPage
              caseId={route.id}
              onBack={() => goto({ kind: 'case', id: route.id })}
              onDone={() => goto({ kind: 'case', id: route.id })}
            />
          )}
          {route.kind === 'decisao' && (
            <DecisaoRecursoPage
              caseId={route.id}
              onBack={() => goto({ kind: 'case', id: route.id })}
              onDone={() => goto({ kind: 'dashboard' })}
            />
          )}
          {route.kind === 'change-password' && (
            <ChangePasswordForm
              minLength={CHANGE_PASSWORD_MIN_LENGTH}
              labels={{
                title: t('app.change_password'),
                intro: t('change_password.intro'),
                currentLabel: t('change_password.current_label'),
                newLabel: t('change_password.new_label', { min: CHANGE_PASSWORD_MIN_LENGTH }),
                confirmLabel: t('change_password.confirm_label'),
                totpLabel: t('change_password.totp_label'),
                submit: t('change_password.submit'),
                submitting: t('change_password.submitting'),
                cancel: t('change_password.cancel'),
                doneTitle: t('change_password.done_title'),
                doneMessage: t('change_password.done_message'),
                doneButton: t('change_password.done_button'),
                errTooShort: t('change_password.errors.too_short', {
                  min: CHANGE_PASSWORD_MIN_LENGTH,
                }),
                errMismatch: t('change_password.errors.mismatch'),
                errSameAsCurrent: t('change_password.errors.same_password'),
                errInvalidTotp: t('change_password.errors.invalid_totp'),
                errInvalidCredentials: t('change_password.errors.invalid_credentials'),
                errNotAuthenticated: t('change_password.errors.not_authenticated'),
                errGeneric: t('change_password.errors.generic'),
              }}
              onSubmit={async (currentPassword, newPassword, totpCode) => {
                const res = await changePassword(currentPassword, newPassword, totpCode);
                if (res.ok) return { ok: true };
                if (res.error === 'invalid_credentials')
                  return { ok: false, error: 'invalid_credentials' };
                if (res.error === 'same_password') return { ok: false, error: 'same_password' };
                if (res.error === 'not_authenticated')
                  return { ok: false, error: 'not_authenticated' };
                return { ok: false, error: 'other' };
              }}
              onDone={() => goto({ kind: 'dashboard' })}
              onCancel={() => goto({ kind: 'dashboard' })}
            />
          )}
        </ErrorBoundary>
      </main>

      {needsPolicyAccept && (
        <PolicyAcceptanceModal
          version={LGP_02_VERSION}
          labels={{
            title: t('policy_modal.title'),
            subtitle: t('policy_modal.subtitle'),
            versionLabel: t('policy_modal.version_label'),
            draftWarning: t('policy_modal.draft_warning'),
            scrollHint: t('policy_modal.scroll_hint'),
            checkbox: t('policy_modal.checkbox'),
            acceptButton: t('policy_modal.accept_button'),
            accepting: t('policy_modal.accepting'),
            logoutButton: t('policy_modal.logout_button'),
            errorNetwork: t('policy_modal.errors.network'),
            errorSubmit: t('policy_modal.errors.submit_failed'),
          }}
          onAccept={async () => {
            const res = await acceptPolicy();
            return res.ok
              ? { ok: true }
              : { ok: false, error: res.error === 'network' ? 'network' : 'submit' };
          }}
          onAccepted={() => setSession(getStoredSession())}
          onLogout={async () => {
            await logout();
            setSession(null);
            goto({ kind: 'login' });
          }}
        />
      )}
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <SessionProvider>
      <AppShell />
    </SessionProvider>
  );
}
